import React, { useEffect } from 'react';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Form from '@cloudscape-design/components/form';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Checkbox from '@cloudscape-design/components/checkbox';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Alert from "@cloudscape-design/components/alert";
import { getApiHeader } from 'src/utils';
import { Participant } from './Participants';
import { ParticipantEventDetails } from './ParticipantEventDetails';
import { Spinner } from '@cloudscape-design/components';

interface ParticipantDetailsProps {
    participant: Participant;
}

export const ParticipantDetails: React.FC<ParticipantDetailsProps> = ({
    participant
}) => {
    const [checked, setChecked] = React.useState(false);
    const headers = getApiHeader();

    const { data: participantDetails, refetch, isLoading } = useQuery({
        queryKey: [`participantDetails-${participant.ParticipantID}`],
        queryFn: async () => {
            const result = await axios.post('https://5c82sz3u4e.execute-api.us-east-1.amazonaws.com/Prod/participantdetails ',
                {
                    participantID: participant.ParticipantID,
                    chestNumber: participant.ChestNum
                }, {
                headers
            });
            return result.data.result;
        },
        enabled: !!participant,
        staleTime: 0,
    });

    const { mutate, data, error, isSuccess, isPending } = useMutation({
        mutationFn: (data: { participantID: string, chestNumber: string; checkIn: number }) => {
            return axios.post('https://ep6mllgvy7.execute-api.us-east-1.amazonaws.com/Prod/checkinparticipant',
                data, {
                headers
            });
        }
    });

    const saveHandler = () => {
        mutate({
            participantID: participant.ParticipantID,
            chestNumber: participant.ChestNum,
            checkIn: Number(checked)
        });
    }

    useEffect(() => {
        setChecked(participantDetails?.checkedIn);
    }, [participantDetails?.checkedIn]);

    useEffect(() => {
        if (isSuccess) {
            refetch();
        }
    }, [isSuccess])

    if(isLoading) {
        return <Spinner size='large' />
    }

    return (
        <SpaceBetween size='l'>
            <ExpandableSection
                defaultExpanded
                variant="container"
                headerText="Participant details"
            >
                <SpaceBetween size='l'>
                    {
                        checked && isSuccess &&  (
                            <Alert type="success">Participant checked-in successfully</Alert>
                        )
                    }
                    {
                        !checked && isSuccess &&  (
                            <Alert type="success">Participant checked-out successfully</Alert>
                        )
                    }
                    {
                        error && (
                            <Alert
                                statusIconAriaLabel="Error"
                                type="error"
                                header="Error checking in Participant"
                            >{error.message}</Alert>
                        )
                    }
                    <Form
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="primary" disabled={isPending} loading={isPending} onClick={saveHandler}>Save</Button>
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween direction="horizontal" size="l">
                            <FormField label="Chest number">
                                <Input disabled value={participantDetails?.chestNumber} placeholder='Chest number' />
                            </FormField>
                            <FormField label="Grade">
                                <Input disabled value={participantDetails?.grade} placeholder='Grade' />
                            </FormField>
                            <FormField label="Participant Phone number">
                                <Input disabled value={participantDetails?.phoneNumber} placeholder='Phone number' />
                            </FormField>
                            <FormField label="First name">
                                <Input disabled value={participantDetails?.firstName} placeholder='First name' />
                            </FormField>
                            <FormField label="Last name">
                                <Input disabled value={participantDetails?.lastName} placeholder='First name' />
                            </FormField>
                            {
                                participantDetails?.checkedIn && (
                                    <FormField label="Check-in time">
                                        <Input disabled value={new Date(participantDetails?.checkedInTime).toLocaleString('en-us', { timeZone: 'America/New_York' })} placeholder='First name' />
                                    </FormField>
                                )
                            }
                            <FormField label="Check-in">
                                <Checkbox checked={checked} onChange={({ detail }) =>
                                    setChecked(detail.checked)
                                }>
                                    Check in
                                </Checkbox>
                            </FormField>
                        </SpaceBetween>
                    </Form>
                </SpaceBetween>
            </ExpandableSection>
            {
                participantDetails && (<ParticipantEventDetails eventsDetails={participantDetails?.events} />)
            }
        </SpaceBetween>
    )
}